import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Table, Alert, Spinner,
    Modal, InputGroup, Badge
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import './Factures.css';

export default function ModifierBonLivraison() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [clients, setClients] = useState([]);
    const [produits, setProduits] = useState([]);
    const [factures, setFactures] = useState([]);
    const [showProduitModal, setShowProduitModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    
    // État du formulaire
    const [bl, setBl] = useState({
        id_client: '',
        id_facture: '',
        date_livraison: '',
        adresse_livraison: '',
        destinataire: '',
        notes: '',
        statut: 'brouillon',
        lignes: []
    });

    useEffect(() => {
        fetchBonLivraison();
        fetchClients();
        fetchProduits();
        fetchFactures();
    }, [id]);

    const fetchBonLivraison = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/bons_livraison.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setBl(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/clients.php',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setClients(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement clients", err);
        }
    };

    const fetchProduits = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/produits.php',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setProduits(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement produits", err);
        }
    };

    const fetchFactures = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/factures.php',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setFactures(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement factures", err);
        }
    };

    const ajouterLigne = (produit) => {
        const nouvelleLigne = {
            id_produit: produit.id_produit,
            reference: produit.reference,
            designation: produit.designation,
            quantite: 1,
            lot: '',
            date_peremption: ''
        };
        setBl({
            ...bl,
            lignes: [...bl.lignes, nouvelleLigne]
        });
        setShowProduitModal(false);
    };

    const supprimerLigne = (index) => {
        const nouvellesLignes = bl.lignes.filter((_, i) => i !== index);
        setBl({ ...bl, lignes: nouvellesLignes });
    };

    const updateLigne = (index, champ, valeur) => {
        const nouvellesLignes = [...bl.lignes];
        nouvellesLignes[index][champ] = valeur;
        setBl({ ...bl, lignes: nouvellesLignes });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!bl.id_client) {
            setError("Veuillez sélectionner un client");
            return;
        }
        
        if (bl.lignes.length === 0) {
            setError("Veuillez ajouter au moins un produit");
            return;
        }

        setSaving(true);
        setError("");

        try {
            const token = localStorage.getItem("token");
            
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/bons_livraison.php?id=${id}`,
                {
                    method: 'PUT',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(bl)
                }
            );

            const result = await response.json();

            if (result.success) {
                setSuccess("Bon de livraison modifié avec succès !");
                setTimeout(() => {
                    navigate('/ventes/blListe');
                }, 2000);
            } else {
                setError(result.message || "Erreur lors de la modification");
            }
        } catch (err) {
            console.error("Erreur:", err);
            setError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/bons_livraison.php?id=${id}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            const result = await response.json();

            if (result.success) {
                setShowDeleteModal(false);
                setSuccess("Bon de livraison supprimé avec succès !");
                setTimeout(() => {
                    navigate('/ventes/blListe');
                }, 2000);
            } else {
                setError(result.message);
                setShowDeleteModal(false);
            }
        } catch (err) {
            setError("Erreur lors de la suppression");
            setShowDeleteModal(false);
        } finally {
            setDeleting(false);
        }
    };

    const getStatutBadge = (statut) => {
        const configs = {
            brouillon: { color: 'secondary', label: 'Brouillon' },
            prepare: { color: 'primary', label: 'Préparé' },
            livre: { color: 'success', label: 'Livré' },
            annule: { color: 'danger', label: 'Annulé' }
        };
        const config = configs[statut] || configs.brouillon;
        return <Badge bg={config.color}>{config.label}</Badge>;
    };

    if (loading) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <Spinner animation="border" variant="primary" />
                    </div>
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="page-icon bg-primary-soft">
                                        <i className="ri-pencil-line text-primary"></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1">Modifier le bon de livraison</h1>
                                        <p className="page-subtitle mb-0">
                                            {bl.numero_bl} • {getStatutBadge(bl.statut)}
                                        </p>
                                    </div>
                                </div>
                                <Button 
                                    variant="outline-secondary" 
                                    onClick={() => navigate('/ventes/blListe')}
                                    className="btn-gradient px-4 py-2"
                                >
                                    <i className="ri-arrow-left-line me-2"></i>
                                    Retour
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {error && (
                        <Alert variant="danger" onClose={() => setError("")} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2"></i>
                            {error}
                        </Alert>
                    )}
                    
                    {success && (
                        <Alert variant="success" className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2"></i>
                            {success}
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                        <Row>
                            {/* Informations générales */}
                            <Col lg={8}>
                                <Card className="border-0 shadow-sm mb-4">
                                    <Card.Header className="bg-primary text-white">
                                        <h5 className="mb-0">
                                            <i className="ri-information-line me-2"></i>
                                            Informations générales
                                        </h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Client <span className="text-danger">*</span></Form.Label>
                                                    <Form.Select
                                                        value={bl.id_client}
                                                        onChange={(e) => {
                                                            const client = clients.find(c => c.id_client === parseInt(e.target.value));
                                                            setBl({
                                                                ...bl,
                                                                id_client: e.target.value,
                                                                adresse_livraison: client?.adresse || '',
                                                                destinataire: client?.raison_sociale || ''
                                                            });
                                                        }}
                                                        required
                                                        disabled={bl.statut !== 'brouillon'}
                                                    >
                                                        <option value="">Sélectionner un client</option>
                                                        {clients.map(client => (
                                                            <option key={client.id_client} value={client.id_client}>
                                                                {client.raison_sociale} ({client.code_client})
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Facture associée</Form.Label>
                                                    <Form.Select
                                                        value={bl.id_facture}
                                                        onChange={(e) => setBl({...bl, id_facture: e.target.value})}
                                                        disabled={bl.statut !== 'brouillon'}
                                                    >
                                                        <option value="">Sans facture</option>
                                                        {factures.map(facture => (
                                                            <option key={facture.id_facture} value={facture.id_facture}>
                                                                {facture.numero_facture} - {facture.client_nom}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Date livraison <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={bl.date_livraison}
                                                        onChange={(e) => setBl({...bl, date_livraison: e.target.value})}
                                                        required
                                                        disabled={bl.statut !== 'brouillon'}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Destinataire</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={bl.destinataire || ''}
                                                        onChange={(e) => setBl({...bl, destinataire: e.target.value})}
                                                        placeholder="Nom du destinataire"
                                                        disabled={bl.statut !== 'brouillon'}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label>Adresse de livraison</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        value={bl.adresse_livraison || ''}
                                                        onChange={(e) => setBl({...bl, adresse_livraison: e.target.value})}
                                                        placeholder="Adresse complète de livraison"
                                                        disabled={bl.statut !== 'brouillon'}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label>Notes</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        value={bl.notes || ''}
                                                        onChange={(e) => setBl({...bl, notes: e.target.value})}
                                                        placeholder="Instructions particulières..."
                                                        disabled={bl.statut !== 'brouillon'}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* Lignes du BL */}
                                <Card className="border-0 shadow-sm">
                                    <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0">
                                            <i className="ri-shopping-cart-line me-2"></i>
                                            Produits à livrer
                                        </h5>
                                        {bl.statut === 'brouillon' && (
                                            <Button 
                                                variant="light" 
                                                size="sm"
                                                onClick={() => setShowProduitModal(true)}
                                            >
                                                <i className="ri-add-line me-1"></i>
                                                Ajouter un produit
                                            </Button>
                                        )}
                                    </Card.Header>
                                    <Card.Body>
                                        {bl.lignes.length > 0 ? (
                                            <div className="table-responsive">
                                                <Table className="align-middle">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th>Référence</th>
                                                            <th>Désignation</th>
                                                            <th className="text-center">Quantité</th>
                                                            <th>Lot</th>
                                                            <th>Date péremption</th>
                                                            {bl.statut === 'brouillon' && <th className="text-center"></th>}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {bl.lignes.map((ligne, index) => (
                                                            <tr key={index}>
                                                                <td>
                                                                    <Badge bg="light" text="dark" className="font-mono">
                                                                        {ligne.reference}
                                                                    </Badge>
                                                                </td>
                                                                <td className="fw-semibold">{ligne.designation}</td>
                                                                <td className="text-center" style={{ width: '100px' }}>
                                                                    <Form.Control
                                                                        type="number"
                                                                        min="1"
                                                                        value={ligne.quantite}
                                                                        onChange={(e) => updateLigne(index, 'quantite', parseInt(e.target.value) || 1)}
                                                                        size="sm"
                                                                        disabled={bl.statut !== 'brouillon'}
                                                                    />
                                                                </td>
                                                                <td style={{ width: '120px' }}>
                                                                    <Form.Control
                                                                        type="text"
                                                                        value={ligne.lot || ''}
                                                                        onChange={(e) => updateLigne(index, 'lot', e.target.value)}
                                                                        placeholder="N° lot"
                                                                        size="sm"
                                                                        disabled={bl.statut !== 'brouillon'}
                                                                    />
                                                                </td>
                                                                <td style={{ width: '130px' }}>
                                                                    <Form.Control
                                                                        type="date"
                                                                        value={ligne.date_peremption || ''}
                                                                        onChange={(e) => updateLigne(index, 'date_peremption', e.target.value)}
                                                                        size="sm"
                                                                        disabled={bl.statut !== 'brouillon'}
                                                                    />
                                                                </td>
                                                                {bl.statut === 'brouillon' && (
                                                                    <td className="text-center">
                                                                        <Button 
                                                                            variant="link" 
                                                                            className="text-danger p-0"
                                                                            onClick={() => supprimerLigne(index)}
                                                                        >
                                                                            <i className="ri-delete-bin-line"></i>
                                                                        </Button>
                                                                    </td>
                                                                )}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-5">
                                                <i className="ri-shopping-cart-line fs-48 text-muted mb-3"></i>
                                                <p className="text-muted">Aucun produit ajouté</p>
                                                {bl.statut === 'brouillon' && (
                                                    <Button 
                                                        variant="outline-primary"
                                                        onClick={() => setShowProduitModal(true)}
                                                    >
                                                        <i className="ri-add-line me-2"></i>
                                                        Ajouter un produit
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Récapitulatif */}
                            <Col lg={4}>
                                <Card className="border-0 shadow-sm sticky-top" style={{ top: '20px' }}>
                                    <Card.Header className="bg-primary text-white">
                                        <h5 className="mb-0">
                                            <i className="ri-calculator-line me-2"></i>
                                            Récapitulatif
                                        </h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="mb-3">
                                            <small className="text-muted d-block">Total articles</small>
                                            <h3 className="mb-0">{bl.lignes.length}</h3>
                                        </div>

                                        <div className="mb-3">
                                            <small className="text-muted d-block">Quantité totale</small>
                                            <h3 className="mb-0">
                                                {bl.lignes.reduce((acc, l) => acc + (l.quantite || 0), 0)}
                                            </h3>
                                        </div>

                                        <hr />

                                        <div className="mb-3">
                                            <Form.Group>
                                                <Form.Label>Statut</Form.Label>
                                                <Form.Select
                                                    value={bl.statut}
                                                    onChange={(e) => setBl({...bl, statut: e.target.value})}
                                                    disabled={bl.statut !== 'brouillon'}
                                                >
                                                    <option value="brouillon">📝 Brouillon</option>
                                                    <option value="prepare">📦 Préparé</option>
                                                    <option value="livre">✅ Livré</option>
                                                    <option value="annule">❌ Annulé</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </div>

                                        <hr />

                                        <div className="d-grid gap-2">
                                            {bl.statut === 'brouillon' && (
                                                <>
                                                    <Button 
                                                        type="submit" 
                                                        variant="primary" 
                                                        size="lg"
                                                        disabled={saving}
                                                    >
                                                        {saving ? (
                                                            <>
                                                                <Spinner size="sm" className="me-2" />
                                                                Mise à jour...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="ri-save-line me-2"></i>
                                                                Mettre à jour
                                                            </>
                                                        )}
                                                    </Button>
                                                    <Button 
                                                        variant="danger" 
                                                        size="lg"
                                                        onClick={() => setShowDeleteModal(true)}
                                                    >
                                                        <i className="ri-delete-bin-line me-2"></i>
                                                        Supprimer
                                                    </Button>
                                                </>
                                            )}
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => navigate('/ventes/blListe')}
                                            >
                                                Annuler
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                </div>
                <Footer />
            </div>

            {/* Modal de sélection des produits */}
            <Modal show={showProduitModal} onHide={() => setShowProduitModal(false)} size="lg" centered className="modern-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="ri-shopping-cart-line me-2 text-primary"></i>
                        Sélectionner un produit
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="table-responsive">
                        <Table hover className="align-middle modern-table">
                            <thead>
                                <tr>
                                    <th>Référence</th>
                                    <th>Désignation</th>
                                    <th className="text-end">Stock</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {produits.map(produit => (
                                    <tr key={produit.id_produit} className="product-row">
                                        <td>
                                            <Badge bg="light" text="dark" className="font-mono">
                                                {produit.reference}
                                            </Badge>
                                        </td>
                                        <td className="fw-semibold">{produit.designation}</td>
                                        <td className="text-end">
                                            <span className={produit.stock_actuel < 1 ? 'text-danger fw-bold' : ''}>
                                                {produit.stock_actuel || 0}
                                            </span>
                                        </td>
                                        <td>
                                            <Button 
                                                size="sm" 
                                                variant="primary"
                                                onClick={() => ajouterLigne(produit)}
                                                className="btn-gradient"
                                                disabled={produit.stock_actuel < 1}
                                            >
                                                <i className="ri-add-line"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Modal de confirmation de suppression */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modern-modal">
                <Modal.Header closeButton>
                    <Modal.Title className="text-danger">
                        <i className="ri-alert-line me-2"></i>
                        Confirmer la suppression
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Êtes-vous sûr de vouloir supprimer ce bon de livraison ?</p>
                    <p className="fw-bold">{bl.numero_bl} - {bl.client_nom}</p>
                    <p className="text-muted small">Cette action est irréversible.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Annuler
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                        {deleting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Suppression...
                            </>
                        ) : (
                            'Confirmer la suppression'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </React.Fragment>
    );
}